import { type JSX, useEffect, useMemo, useState } from 'react';

import { APP_LIBRARY, APP_LIBRARY_CATEGORIES, type AppLibraryEntry } from './AppLibrary.js';

export interface AppLibraryPickerProps {
  /**
   * Fires when the user selects an app. The caller should patch the
   * tile's packageName (and optionally label, if it was blank).
   */
  onPick: (entry: AppLibraryEntry) => void;
  onClose: () => void;
}

/**
 * Modal that lists the curated app catalog grouped by category, with
 * a search box on top. Lets a tile-editor user pick from the catalog
 * instead of remembering or googling a package name.
 *
 * Two affordances kept deliberately simple:
 *   - Search filters by label OR package name (case-insensitive)
 *   - Click a row → onPick(entry) + close
 *
 * The list is keyboard-navigable via the standard <button> tab order;
 * Escape closes the modal (handled in the effect below). Background
 * click closes too — same pattern as TierDetailModal.
 */
export function AppLibraryPicker({ onPick, onClose }: AppLibraryPickerProps): JSX.Element {
  const [query, setQuery] = useState('');

  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q === '') return APP_LIBRARY;
    return APP_LIBRARY.filter(
      (e) =>
        e.label.toLowerCase().includes(q) || e.packageName.toLowerCase().includes(q),
    );
  }, [query]);

  return (
    <div
      className="app-library__backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="app-library-title"
      onClick={onClose}
    >
      <div
        className="app-library__modal"
        onClick={(e) => e.stopPropagation()}
        data-testid="app-library-modal"
      >
        <header className="app-library__head">
          <h2 id="app-library-title" className="app-library__title">
            Pick an app
          </h2>
          <button
            type="button"
            className="app-library__close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </header>
        <input
          className="app-library__search"
          type="text"
          placeholder="Search by name or package..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          autoFocus
          data-testid="app-library-search"
        />
        <div className="app-library__list">
          {APP_LIBRARY_CATEGORIES.map((cat) => {
            const inCat = filtered.filter((e) => e.category === cat);
            if (inCat.length === 0) return null;
            return (
              <section key={cat} className="app-library__section">
                <h3 className="app-library__section-title">{cat}</h3>
                <ul className="app-library__items">
                  {inCat.map((e) => (
                    <li key={e.packageName}>
                      <button
                        type="button"
                        className="app-library__item"
                        onClick={() => {
                          onPick(e);
                          onClose();
                        }}
                        data-testid={`app-library-${e.packageName}`}
                      >
                        <span className="app-library__item-label">{e.label}</span>
                        <span className="app-library__item-pkg">{e.packageName}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            );
          })}
          {filtered.length === 0 && (
            <p className="app-library__empty">
              No matches. The library covers ~30 common apps — type the package
              name directly in the field if your app isn't here.
            </p>
          )}
        </div>
        <footer className="app-library__foot">
          <p className="app-library__foot-note">
            Picking an app fills in the package name; you can still rename the
            tile label after. Not seeing your app? Type the package directly —
            the runtime accepts any valid Android package id.
          </p>
        </footer>
      </div>
    </div>
  );
}
